/**
 * HRSN domain spec — mirrors backend src/screenings/hrsn-spec.ts.
 *
 * Frontend source of truth for what the agent sees:
 *   - Title (UI heading)
 *   - prompt (script the agent reads aloud)
 *   - hooks (gentle conversational follow-ups, optional)
 *   - per-domain answer shape and default
 *   - per-domain validator (lightweight; backend is the safety net)
 *   - positive_finding logic (computed locally for UI badges)
 *
 * If FindHelp updates the form, change here AND in backend's hrsn-spec.ts.
 */

import type { HrsnDomain } from '@/types/api';

export const DOMAIN_ORDER: HrsnDomain[] = [
  'living_situation',
  'housing_conditions',
  'utilities',
  'food',
  'transportation',
  'career',
  'school',
  'safety',
  'physical_ability',
];

export interface DomainSpec {
  domain: HrsnDomain;
  title: string;
  titleEs: string;
  prompt: string;
  promptEs: string;
  hooks?: string[];
  hooksEs?: string[];
}

export const DOMAIN_SPECS: Record<HrsnDomain, DomainSpec> = {
  living_situation: {
    domain: 'living_situation',
    title: 'Living situation',
    titleEs: 'Vivienda',
    prompt:
      "Let's start with your home. Do you have a steady place to live, or are you worried about losing your housing?",
    hooks: ['If worried: are you renting? Have you received notices?', 'If fine: how are the conditions?'],
    promptEs:
      'Empecemos por su casa. ¿Tiene un lugar estable donde vivir, o le preocupa perder su vivienda?',
    hooksEs: ['Si está preocupado: ¿está alquilando? ¿Ha recibido avisos?', 'Si está bien: ¿cómo están las condiciones?'],
  },
  housing_conditions: {
    domain: 'housing_conditions',
    title: 'Housing conditions',
    titleEs: 'Condiciones de la vivienda',
    prompt:
      'Thinking about the place you live, do you have problems with any of the following? (ask one by one — pests, mold, heat, oven, smoke detectors, water leaks, lead)',
    hooks: ['How long have you had that problem?', 'Have you tried to get help with it?'],
    promptEs:
      'Pensando en el lugar donde vive, ¿tiene problemas con alguno de los siguientes? (pregunte uno por uno — plagas, moho, calefacción, estufa, detectores de humo, fugas de agua, plomo)',
    hooksEs: ['¿Hace cuánto tiene ese problema?', '¿Ha intentado conseguir ayuda?'],
  },
  utilities: {
    domain: 'utilities',
    title: 'Utilities',
    titleEs: 'Servicios públicos',
    prompt:
      'In the past 12 months, has any electric, gas, oil, or water company threatened to shut off services in your home?',
    hooks: ['Which service?', 'Were you able to resolve it?'],
    promptEs:
      'En los últimos 12 meses, ¿alguna compañía de electricidad, gas, petróleo o agua le amenazó con cortar el servicio?',
    hooksEs: ['¿Cuál servicio?', '¿Pudo resolverlo?'],
  },
  food: {
    domain: 'food',
    title: 'Food',
    titleEs: 'Alimentación',
    prompt:
      'In the past 12 months, were there times when you worried your food would run out before you had money to buy more, or that the food you bought didn’t last?',
    hooks: ['Do you have access to SNAP, WIC, or any food program?'],
    promptEs:
      'En los últimos 12 meses, ¿hubo momentos en que le preocupó que se le acabara la comida antes de tener dinero para comprar más, o que la comida que compraba no le rindiera?',
    hooksEs: ['¿Tiene acceso a SNAP, WIC o algún programa de alimentos?'],
  },
  transportation: {
    domain: 'transportation',
    title: 'Transportation',
    titleEs: 'Transporte',
    prompt:
      'Has lack of reliable transportation kept you from medical appointments, work, or things needed for daily living?',
    hooks: ['How do you usually get around?'],
    promptEs:
      'La falta de transporte confiable, ¿le ha impedido ir a citas médicas, al trabajo, o cosas necesarias para el día a día?',
    hooksEs: ['¿Cómo se transporta normalmente?'],
  },
  career: {
    domain: 'career',
    title: 'Career',
    titleEs: 'Carrera',
    prompt: 'Are you currently working? Would you like help finding or keeping a job?',
    hooks: ['What do you do for work?', 'What kind of job would you be interested in?'],
    promptEs:
      '¿Está trabajando actualmente? ¿Le interesaría ayuda para encontrar o mantener un empleo?',
    hooksEs: ['¿En qué trabaja?', '¿Qué tipo de trabajo le interesaría?'],
  },
  school: {
    domain: 'school',
    title: 'School',
    titleEs: 'Educación',
    prompt:
      'Would you be interested in help with school or training? For example, job training or getting your GED?',
    promptEs:
      '¿Le interesaría ayuda con estudios o capacitación? Por ejemplo, entrenamiento laboral o sacar su GED.',
  },
  safety: {
    domain: 'safety',
    title: 'Safety',
    titleEs: 'Seguridad',
    prompt:
      'Because violence affects many people\'s health, the State asks us a few standard safety questions. You can decline any of them.',
    hooks: ['Be respectful. If real distress, mark "Screener did not ask".'],
    promptEs:
      'Como la violencia afecta la salud de muchas personas, el Estado pide hacerle algunas preguntas estándar de seguridad. Puede declinar cualquiera.',
    hooksEs: ['Sea respetuoso. Si hay angustia real, marque "El entrevistador no preguntó".'],
  },
  physical_ability: {
    domain: 'physical_ability',
    title: 'Physical ability',
    titleEs: 'Capacidad física',
    prompt: 'Do you have serious difficulty walking, climbing stairs, dressing, or bathing?',
    hooks: ['If yes: do you need help at home?'],
    promptEs:
      '¿Tiene serias dificultades para caminar, subir escaleras, vestirse o bañarse?',
    hooksEs: ['Si dice que sí: ¿necesita ayuda en casa?'],
  },
};

// ---------------------------------------------------------------------------
// Per-domain answer types and option lists
// ---------------------------------------------------------------------------

export const HOUSING_CONDITION_OPTIONS = [
  { value: 'pests', label: 'Pests (bugs, ants, mice)', labelEs: 'Plagas (insectos, hormigas, ratones)' },
  { value: 'mold', label: 'Mold', labelEs: 'Moho' },
  { value: 'lead', label: 'Lead paint or pipes', labelEs: 'Pintura o tuberías con plomo' },
  { value: 'no_heat', label: 'Lack of heat', labelEs: 'Falta de calefacción' },
  { value: 'oven_broken', label: 'Oven or stove not working', labelEs: 'Estufa u horno no funciona' },
  { value: 'smoke_detector', label: 'Smoke detectors missing or broken', labelEs: 'Detectores de humo faltantes o rotos' },
  { value: 'water_leaks', label: 'Water leaks', labelEs: 'Fugas de agua' },
  { value: 'none', label: 'None of the above', labelEs: 'Ninguna de las anteriores' },
] as const;

export const FOOD_FREQUENCY_OPTIONS = [
  { value: 'often_true', label: 'Often true', labelEs: 'A menudo verdadero' },
  { value: 'sometimes_true', label: 'Sometimes true', labelEs: 'A veces verdadero' },
  { value: 'never_true', label: 'Never true', labelEs: 'Nunca verdadero' },
] as const;

export const SAFETY_LEVEL_OPTIONS = [
  { value: 'never_1', label: 'Never (1)', labelEs: 'Nunca (1)', score: 1 },
  { value: 'rarely_2', label: 'Rarely (2)', labelEs: 'Rara vez (2)', score: 2 },
  { value: 'sometimes_3', label: 'Sometimes (3)', labelEs: 'A veces (3)', score: 3 },
  { value: 'fairly_often_4', label: 'Fairly often (4)', labelEs: 'Bastante seguido (4)', score: 4 },
  { value: 'frequently_5', label: 'Frequently (5)', labelEs: 'Frecuentemente (5)', score: 5 },
  { value: 'declined', label: 'Declined to answer', labelEs: 'Declinó responder', score: null },
  { value: 'not_asked', label: 'Screener did not ask', labelEs: 'El entrevistador no preguntó', score: null },
] as const;

export const PHYSICAL_ABILITY_OPTIONS = [
  { value: 'yes', label: 'Yes', labelEs: 'Sí' },
  { value: 'no', label: 'No', labelEs: 'No' },
  { value: 'declined', label: 'Declined', labelEs: 'Declinó' },
  { value: 'not_asked', label: 'Not asked', labelEs: 'No preguntado' },
] as const;

// ---------------------------------------------------------------------------
// Default empty answers + validation (mirrors backend)
// ---------------------------------------------------------------------------

export type DomainAnswer = Record<string, unknown>;

export function defaultAnswerFor(domain: HrsnDomain): DomainAnswer {
  switch (domain) {
    case 'housing_conditions':
      return { selections: [] };
    case 'food':
      return { worried_running_out: '', food_didnt_last: '' };
    case 'safety':
      return {
        physical_hurt: '',
        insult: '',
        threaten: '',
        scream_curse: '',
        total_score: 0,
      };
    case 'physical_ability':
      return { walking_stairs: '', dressing_bathing: '' };
    default:
      return { answer: '' };
  }
}

/** True only when the answer is fully filled and valid. */
export function isDomainValid(domain: HrsnDomain, a: DomainAnswer): boolean {
  switch (domain) {
    case 'living_situation':
      return ['steady', 'worried', 'unstable'].includes(String(a.answer));
    case 'housing_conditions': {
      const sel = a.selections as string[] | undefined;
      if (!Array.isArray(sel) || sel.length === 0) return false;
      const allowed = HOUSING_CONDITION_OPTIONS.map((o) => o.value as string);
      if (sel.some((s) => !allowed.includes(s))) return false;
      if (sel.includes('none') && sel.length > 1) return false;
      return true;
    }
    case 'utilities':
      return ['yes', 'no', 'already_shut_off'].includes(String(a.answer));
    case 'food': {
      const ok = (v: unknown) =>
        ['often_true', 'sometimes_true', 'never_true'].includes(String(v));
      return ok(a.worried_running_out) && ok(a.food_didnt_last);
    }
    case 'transportation':
      return ['yes', 'no'].includes(String(a.answer));
    case 'career':
      return ['help_finding', 'help_keeping', 'no_help'].includes(String(a.answer));
    case 'school':
      return ['yes', 'no'].includes(String(a.answer));
    case 'safety': {
      const allowed = SAFETY_LEVEL_OPTIONS.map((o) => o.value as string);
      const fields = ['physical_hurt', 'insult', 'threaten', 'scream_curse'] as const;
      return fields.every((f) => allowed.includes(String(a[f])));
    }
    case 'physical_ability': {
      const allowed = ['yes', 'no', 'declined', 'not_asked'];
      return allowed.includes(String(a.walking_stairs)) && allowed.includes(String(a.dressing_bathing));
    }
  }
}

/** Computed positive_finding; mirrors backend computePositiveFinding. */
export function computePositiveFinding(domain: HrsnDomain, a: DomainAnswer): boolean {
  switch (domain) {
    case 'living_situation':
      return a.answer === 'worried' || a.answer === 'unstable';
    case 'housing_conditions': {
      const sel = (a.selections as string[]) ?? [];
      return !(sel.length === 1 && sel[0] === 'none');
    }
    case 'utilities':
      return a.answer === 'yes' || a.answer === 'already_shut_off';
    case 'food': {
      const positive = (v: unknown) => v === 'often_true' || v === 'sometimes_true';
      return positive(a.worried_running_out) || positive(a.food_didnt_last);
    }
    case 'transportation':
      return a.answer === 'yes';
    case 'career':
      return a.answer === 'help_finding' || a.answer === 'help_keeping';
    case 'school':
      return a.answer === 'yes';
    case 'safety':
      return Number(a.total_score ?? 0) >= 11;
    case 'physical_ability':
      return a.walking_stairs === 'yes' || a.dressing_bathing === 'yes';
  }
}

/** Sum the 4 numeric safety levels. Declined/not_asked don't contribute. */
export function computeSafetyTotal(a: DomainAnswer): number {
  let total = 0;
  for (const k of ['physical_hurt', 'insult', 'threaten', 'scream_curse'] as const) {
    const v = a[k];
    const opt = SAFETY_LEVEL_OPTIONS.find((o) => o.value === v);
    if (opt && opt.score !== null) total += opt.score;
  }
  return total;
}
